/**
 * Where the API lives, from where the app is being served.
 *
 * THE PROBLEM THIS SOLVES IS A COOKIE ONE, not a convenience one. The session is
 * a `SameSite=Lax` cookie, and Lax is deliberately NOT sent on cross-site
 * fetches — only on top-level navigation. So the API has to be on the same
 * REGISTRABLE DOMAIN as the app or the browser silently withholds the session
 * and every shell asks you to log in again.
 *
 * In production that is already true: `beats.lumnian.com` calling
 * `api.lumnian.com` is same-site. In development it was not — the shells run on
 * `*.lumnian.test` and the API answered on `localhost`, which is a different
 * site, so the cookie was never sent and each shell demanded its own login. The
 * single sign-on looked broken when it was the address that was wrong.
 *
 * So: derive `api.<same domain>` from wherever we are being served. Local
 * development becomes same-site by construction and behaves like production
 * rather than approximating it, with no per-developer `.env`.
 *
 * `REACT_APP_API_URL` still wins when set — the production image bakes it in
 * explicitly (see the admin Dockerfile), and pointing a local app at a remote
 * API stays possible.
 *
 * Pure and dependency-free so CRA's jest can test it.
 */

/** The API base URL for an app served from `hostname` at `origin`. */
export function apiOriginFor(
  hostname: string,
  origin: string,
  envUrl?: string,
): string {
  // An explicit setting always wins. Production bakes one in.
  if (envUrl) return envUrl.replace(/\/+$/, '');

  const labels = (hostname || '').split('.');
  // Bare `localhost`, an IP, anything with no domain to graft onto: there is no
  // `api.<domain>` to build, so keep the historical default. Same-site is not a
  // problem here — a single host has nothing to be cross-site with.
  if (labels.length < 2 || /^\d+$/.test(labels[labels.length - 1])) {
    return 'http://localhost:8000';
  }

  const domain = labels.slice(1).join('.');
  const url = new URL(origin);
  url.hostname = `api.${domain}`;
  // A port on the page means development, where the API is on 8000. A page on a
  // standard port means production, where the API is on the standard port too —
  // guessing 8000 there would point at nothing.
  url.port = url.port ? '8000' : '';
  return url.origin;
}
